import React from "react";

/* ---------------- Buttons ---------------- */
export function PrimaryButton({ children, icon: Icon, onClick, type = "button", disabled, className = "", ...rest }) {
  return (
    <button
      type={type} onClick={onClick} disabled={disabled}
      className={[
        "inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-accent text-white shadow-soft transition-colors",
        disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-accent-deep",
        className
      ].join(" ")}
      style={{ fontSize: 12.5, fontWeight: 500 }}
      {...rest}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </button>
  );
}

export function QuietButton({ children, icon: Icon, onClick, type = "button", disabled, className = "", ...rest }) {
  return (
    <button
      type={type} onClick={onClick} disabled={disabled}
      className={[
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line bg-surface text-ink-2 transition-colors",
        disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-surface-2",
        className
      ].join(" ")}
      style={{ fontSize: 12.5 }}
      {...rest}
    >
      {Icon ? <Icon size={13} /> : null}
      {children}
    </button>
  );
}

export function GhostButton({ children, icon: Icon, onClick, type = "button", ...rest }) {
  return (
    <button
      type={type} onClick={onClick}
      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-ink-3 hover:bg-surface-2 hover:text-ink transition-colors"
      style={{ fontSize: 12.5 }}
      {...rest}
    >
      {Icon ? <Icon size={13} /> : null}
      {children}
    </button>
  );
}

/* ---------------- Inputs ---------------- */
export function Field({ label, hint, error, children, required }) {
  return (
    <label className="flex flex-col gap-1.5">
      {label && (
        <span className="col-head" style={{ fontSize: 10 }}>
          {label}{required && <span className="text-note-red ml-1">*</span>}
        </span>
      )}
      {children}
      {error && (
        <span className="text-note-red" style={{ fontSize: 11 }}>{error}</span>
      )}
      {hint && !error && (
        <span className="text-ink-4" style={{ fontSize: 11 }}>{hint}</span>
      )}
    </label>
  );
}

/**
 * Site-wide focus handler: when the user clicks (or tabs) into an editable
 * field that already contains a value, the entire value is selected so a
 * fresh keystroke replaces it. Wired into Input + NumberInput by default.
 *
 * LT-18c inline-field focus fix (2026-09-12): NO-OP on empty fields.
 * The deferred `.select()` used to race the first typed character on an
 * empty field — click, first key typed, rAF fires and selects that one
 * character, next key REPLACES it, so "MA2320" lost its M. There is
 * nothing to select on an empty field, so we return early. On non-empty
 * fields the rAF still fires and the "select existing so a fresh keystroke
 * replaces it" contract still holds (any human pause > ~16ms between
 * click and first key is enough).
 */
export function selectAllOnFocus(e) {
  const t = e.target;
  if (!t || typeof t.select !== "function") return;
  const cur = t.value;
  if (cur === undefined || cur === null || cur === "") return;
  requestAnimationFrame(() => {
    try { t.select(); } catch (_) { /* noop */ }
  });
}

export function Input({ value, onChange, placeholder, type = "text", mono = false, disabled, readOnly, onFocus, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      spellCheck={type === "text"}
      onFocus={(e) => { selectAllOnFocus(e); onFocus?.(e); }}
      className={[
        "h-9 px-3 rounded-md border border-line bg-surface text-ink",
        "focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent",
        "disabled:bg-surface-2 disabled:text-ink-3 disabled:cursor-not-allowed",
        readOnly ? "bg-surface-2 text-ink-3 cursor-default" : "",
        mono ? "font-mono tnum" : ""
      ].join(" ")}
      style={{ fontSize: 13 }}
      {...rest}
    />
  );
}

/**
 * NumberInput — site-wide standard:
 *  - Renders empty when value is null / undefined / 0 (so we don't display
 *    leftover defaults like "0" or "1"). Internally still treats null as 0.
 *  - Selects existing value on focus so a fresh keystroke replaces it.
 *  - Returns null (not 0) when cleared so the caller can distinguish empty
 *    from "explicitly zero".
 *
 *  showZero={true} forces 0 to render (use for read-only display fields).
 */
export function NumberInput({
  value, onChange, step = "0.01", prefix, suffix, disabled, readOnly,
  showZero = false, placeholder = "0.0", onFocus, ...rest
}) {
  const display = (() => {
    if (value === null || value === undefined || value === "") return "";
    if (!showZero && Number(value) === 0) return "";
    return value;
  })();
  // Dynamic side-padding: reserve space that scales with prefix/suffix
  // length so long units ("operatives", "tenders", "/m²") don't overlap
  // the digits. Empirically ~6.5px per character at 11px suffix font +
  // 14px gutter. Capped at 96px so a rogue label can't eat the input.
  const _sidePad = (label, base) => {
    if (!label) return 12;
    const est = 14 + Math.round(String(label).length * 6.5);
    return Math.min(96, Math.max(base, est));
  };
  const padLeft  = _sidePad(prefix, 24);
  const padRight = _sidePad(suffix, 28);
  return (
    <div className="relative">
      {prefix && (
        <span
          className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
          style={{ fontSize: 12 }}
        >
          {prefix}
        </span>
      )}
      <input
        type="number"
        step={step}
        disabled={disabled}
        readOnly={readOnly}
        value={display}
        placeholder={placeholder}
        onFocus={(e) => { selectAllOnFocus(e); onFocus?.(e); }}
        onChange={(e) =>
          onChange?.(e.target.value === "" ? null : Number(e.target.value))
        }
        // LT-17 §8b — every money field carries these two attributes so
        // password managers (1Password, Bitwarden, LastPass, Dashlane)
        // don't inject their padlock icon into the field. Set on the
        // component itself, not per call-site, so every money input in
        // the app is covered at once.
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
        autoComplete="off"
        className={[
          "h-9 rounded-md border border-line bg-surface text-ink font-mono tnum text-right",
          "focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent",
          "disabled:bg-surface-2 disabled:text-ink-3 disabled:cursor-not-allowed",
          readOnly ? "bg-surface-2 text-ink-3 cursor-default" : "",
          "w-full"
        ].join(" ")}
        style={{ fontSize: 13, paddingLeft: padLeft, paddingRight: padRight }}
        {...rest}
      />
      {suffix && (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
          style={{ fontSize: 11 }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

export function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      spellCheck="true"
      className="px-3 py-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-y"
      style={{ fontSize: 13, lineHeight: "18px" }}
    />
  );
}

/**
 * Select — site-wide standard:
 *  - Custom-rendered popover (not native <select>) so it can flip up if it
 *    would overflow the viewport, and float above siblings (z-50) without
 *    being clipped by relative-positioned containers.
 *  - Native fallback is preserved for accessibility — keyboard arrows and
 *    Escape both work via the custom listbox.
 *
 *  options: array of { value, label } OR plain strings (auto-coerced).
 */
export function Select({ value, onChange, options, placeholder = "Select…", disabled, ...rest }) {
  const [open, setOpen] = React.useState(false);
  const [openUp, setOpenUp] = React.useState(false);
  const wrapRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  const opts = React.useMemo(
    () => (options || []).map((o) =>
      typeof o === "string" ? { value: o, label: o } : o
    ),
    [options]
  );
  const selected = opts.find((o) => o.value === value);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Decide flip direction whenever we open
  React.useEffect(() => {
    if (!open || !buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    setOpenUp(spaceBelow < 240 && spaceAbove > spaceBelow);
  }, [open]);

  // Pull data-testid out of rest so we can attach it to the trigger BUTTON
  // (instead of the wrapper div). This makes Playwright .click() land on the
  // actual interactive element rather than its container, which avoids subtle
  // bugs where the click target can't be confidently identified by automation.
  const { "data-testid": testid, ...wrapperRest } = rest || {};

  return (
    <div ref={wrapRef} className="relative" {...wrapperRest}>
      <button
        type="button"
        ref={buttonRef}
        disabled={disabled}
        onClick={() => !disabled && setOpen((x) => !x)}
        data-testid={testid}
        className={[
          "h-9 w-full px-3 rounded-md border border-line bg-surface text-left text-ink",
          "inline-flex items-center justify-between gap-2",
          "focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent",
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-line"
        ].join(" ")}
        style={{ fontSize: 13 }}
      >
        <span className={selected ? "text-ink" : "text-ink-3"}>
          {selected ? selected.label : placeholder}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" className="text-ink-3 flex-shrink-0">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div
          className={[
            "absolute left-0 right-0 z-50 bg-surface border border-line rounded-md shadow-card overflow-auto",
            openUp ? "bottom-full mb-1" : "top-full mt-1"
          ].join(" ")}
          style={{ maxHeight: 240 }}
          data-testid={testid ? `${testid}-popover` : undefined}
        >
          {opts.length === 0 && (
            <div className="px-3 py-2 text-ink-3" style={{ fontSize: 12.5 }}>No options</div>
          )}
          {opts.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                // Use onMouseDown so the option fires BEFORE any blur/document
                // mousedown handlers, eliminating click-eats-event timing bugs.
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange?.(o.value);
                  setOpen(false);
                }}
                data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                className={[
                  "w-full text-left px-3 py-1.5 hover:bg-surface-2 transition-colors",
                  active ? "bg-accent-soft text-accent font-medium" : "text-ink"
                ].join(" ")}
                style={{ fontSize: 12.5 }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ToggleGroup({ value, onChange, options }) {
  return (
    <div className="inline-flex p-0.5 rounded-md border border-line bg-surface-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={[
            "h-7 px-3 rounded transition-colors",
            value === o.value
              ? "bg-surface text-ink shadow-soft"
              : "text-ink-3 hover:text-ink"
          ].join(" ")}
          style={{ fontSize: 12, fontWeight: value === o.value ? 600 : 500 }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Tags ---------------- */
export function TagChip({ label, onRemove, tone = "neutral" }) {
  const tones = {
    neutral: { bg: "#F0EEE6", fg: "#3D3D3D", border: "#E2DDD2" },
    accent:  { bg: "#E8EFE9", fg: "#2F4F3E", border: "#D6E2D8" },
    warning: { bg: "#FEF3E2", fg: "#B45309", border: "#F2DFB8" }
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      className="inline-flex items-center gap-1 rounded"
      style={{
        background: t.bg, color: t.fg,
        border: `1px solid ${t.border}`,
        padding: "2px 7px", fontSize: 11, fontWeight: 500
      }}
    >
      {label}
      {onRemove && (
        <button
          onClick={onRemove}
          className="opacity-60 hover:opacity-100"
          style={{ fontSize: 13, lineHeight: 1 }}
        >×</button>
      )}
    </span>
  );
}

/**
 * MultiSelect — supports a fixed `options` list AND an `allowCustom` mode
 * that lets the user type a free-form tag and press Enter.
 *  - Popover floats above siblings (z-50) and flips up when needed.
 */
export function MultiSelect({
  value = [], onChange, options = [], tone = "neutral",
  placeholder = "Add…", allowCustom = false
}) {
  const [open, setOpen] = React.useState(false);
  const [openUp, setOpenUp] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const wrapRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  const remaining = React.useMemo(() => {
    const lower = (value || []).map((v) => String(v).toLowerCase());
    let out = options.filter((o) => !lower.includes(String(o).toLowerCase()));
    if (draft.trim()) {
      const q = draft.toLowerCase();
      out = out.filter((o) => String(o).toLowerCase().includes(q));
    }
    return out;
  }, [options, value, draft]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  React.useEffect(() => {
    if (!open || !buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    setOpenUp(spaceBelow < 240 && spaceAbove > spaceBelow);
  }, [open]);

  const addTag = (raw) => {
    const v = String(raw || "").trim();
    if (!v) return;
    const lower = (value || []).map((x) => String(x).toLowerCase());
    if (lower.includes(v.toLowerCase())) return;
    onChange([...(value || []), v]);
    setDraft("");
    // LT-39 — one pick closes the list (Chris: "close automatically once a
    // selection is made"); click or type to reopen for the next one.
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div
        className="flex flex-wrap items-center gap-1.5 min-h-[36px] p-1.5 rounded-md border border-line bg-surface focus-within:ring-2 focus-within:ring-accent/30 focus-within:border-accent"
        ref={buttonRef}
      >
        {(value || []).map((v) => (
          <TagChip
            key={v} label={v} tone={tone}
            onRemove={() => onChange(value.filter((x) => x !== v))}
          />
        ))}
        <input
          type="text"
          value={draft}
          placeholder={(value || []).length === 0 ? placeholder : ""}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          data-testid="multiselect-input"
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (allowCustom && draft.trim()) addTag(draft);
              else if (remaining.length > 0) addTag(remaining[0]);
            } else if (e.key === "Backspace" && !draft && (value || []).length > 0) {
              onChange(value.slice(0, -1));
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          className="flex-1 min-w-[80px] bg-transparent border-0 outline-none text-ink"
          style={{ fontSize: 12.5 }}
        />
      </div>
      {open && (remaining.length > 0 || (allowCustom && draft.trim())) && (
        <div
          className={[
            "absolute z-50 left-0 right-0 bg-surface border border-line rounded-md shadow-card overflow-auto",
            openUp ? "bottom-full mb-1" : "top-full mt-1"
          ].join(" ")}
          style={{ maxHeight: 240 }}
        >
          {remaining.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => addTag(o)}
              className="w-full text-left px-3 py-1.5 hover:bg-surface-2 text-ink"
              style={{ fontSize: 12.5 }}
            >
              {o}
            </button>
          ))}
          {allowCustom && draft.trim() &&
            !remaining.some((o) => String(o).toLowerCase() === draft.toLowerCase()) && (
            <button
              type="button"
              onClick={() => addTag(draft)}
              className="w-full text-left px-3 py-1.5 hover:bg-surface-2 text-accent border-t border-line-soft inline-flex items-center gap-1.5"
              style={{ fontSize: 12.5 }}
            >
              <span>+</span> Create "{draft.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Page header / sections ---------------- */
export function PageHeader({ title, subtitle, actions, breadcrumb }) {
  return (
    <>
      {breadcrumb && (
        <div className="flex items-center gap-1.5 text-ink-3 mb-3" style={{ fontSize: 12 }}>
          {breadcrumb}
        </div>
      )}
      <div className="flex items-end justify-between gap-6 mb-7">
        <div>
          <h1 className="font-display text-ink" style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {title}
          </h1>
          {subtitle && <p className="text-ink-3 mt-1" style={{ fontSize: 13 }}>{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </>
  );
}

/**
 * StickyHeader — site-wide pattern for long-form edit pages.
 * Renders a slim sticky bar at the top of the page (below the global TopNav)
 * containing breadcrumb, page title, optional status badge, and primary
 * actions. Use INSIDE the `app-container` page wrapper.
 *
 *  Usage:
 *  <StickyHeader
 *    breadcrumb={<>...</>}
 *    title="Edit: Legacy flat-rate item"
 *    status={<SaveBadge ... />}
 *    actions={<><QuietButton>Cancel</QuietButton><PrimaryButton>Save</PrimaryButton></>}
 *  />
 */
export function StickyHeader({ breadcrumb, title, status, actions, className = "" }) {
  return (
    <div
      data-testid="sticky-header"
      className={[
        "sticky z-40 -mx-[clamp(12px,1.5vw,48px)] px-[clamp(12px,1.5vw,48px)] py-3",
        "bg-bg/85 backdrop-blur-sm border-b border-line",
        className
      ].join(" ")}
      style={{ top: 56 /* height of TopNav */ }}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {breadcrumb && (
            <div className="flex items-center gap-1.5 text-ink-3 flex-shrink-0" style={{ fontSize: 12 }}>
              {breadcrumb}
            </div>
          )}
          <h1 className="font-display text-ink truncate" style={{ fontSize: 16, fontWeight: 600 }}>
            {title}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {status}
          {actions}
        </div>
      </div>
    </div>
  );
}

export function Card({ children, className = "", allowOverflow = false }) {
  return (
    <div className={["bg-surface border border-line rounded-lg shadow-card", allowOverflow ? "" : "overflow-hidden", className].join(" ")}>
      {children}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      {Icon && (
        <div className="w-11 h-11 rounded-full bg-surface-2 flex items-center justify-center text-ink-3 mb-3">
          <Icon size={18} />
        </div>
      )}
      <h3 className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>{title}</h3>
      {hint && <p className="text-ink-3 mt-1.5" style={{ fontSize: 12.5, maxWidth: 360 }}>{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Modal — lightweight, single-screen overlay used by the Library Edit save
 * validator and elsewhere as a generic confirm/alert pattern.
 *
 *  - Click backdrop or press Escape to close (calls onClose).
 *  - Pressing Escape while focus is inside an input also closes.
 *  - Use slot props `header`, `body`, and `footer` for layout flexibility.
 */
/* LT-39 — only the top-most open overlay reacts to Escape, so closing a
   nested dialog (or the PDF viewer) never closes the one underneath. */
const overlayStack = [];
export function useTopmostEscape(open, onClose) {
  const idRef = React.useRef(null);
  if (idRef.current === null) idRef.current = {};
  React.useEffect(() => {
    if (!open) return undefined;
    const id = idRef.current;
    overlayStack.push(id);
    return () => { const i = overlayStack.indexOf(id); if (i >= 0) overlayStack.splice(i, 1); };
  }, [open]);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && overlayStack[overlayStack.length - 1] === idRef.current) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

export function Modal({ open, onClose, title, children, footer, maxWidth = 560, testid = "modal" }) {
  useTopmostEscape(open, onClose);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-ink/40 flex items-start justify-center pt-20 px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid={`${testid}-backdrop`}
    >
      <div
        className="w-full bg-surface border border-line rounded-lg shadow-card overflow-hidden"
        style={{ maxWidth }}
        data-testid={testid}
      >
        {title && (
          <div className="px-5 py-3.5 border-b border-line flex items-center justify-between">
            <h2 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-ink-3 hover:text-ink"
              data-testid={`${testid}-close`}
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 14 14"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
            </button>
          </div>
        )}
        <div className="p-5">
          {children}
        </div>
        {footer && (
          <div className="px-5 py-3.5 border-t border-line bg-surface-2/60 flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
